import type {
  EntityInput,
  ReportFilterInput,
  ReportRequest,
  ReportTypeEnum,
} from '@revelio/data-access';
import type { TreeItem } from '@revelio/iso-utility';

import { combineCustomSkillValues } from '../02-entity-configuration/mw-reports/report-entity-selections/custom-skills.helpers';
import {
  EntityType,
  type IReportEntityMap,
  type ReportEntityConfigColumn,
} from '../02-entity-configuration/mw-reports/utils';
import type {
  EntitySelectionItem,
  ReportBuilderDeliverable,
} from '../report-builder.model';

const groupSelectionsByListId = (
  selections?: EntitySelectionItem
): Record<string, TreeItem<string>[]> => {
  if (!selections) {
    return {};
  }
  return Object.values(selections).reduce(
    (grouped, selectionItem) => {
      const { selectionListId } = selectionItem;

      if (!grouped[selectionListId]) {
        grouped[selectionListId] = [];
      }

      grouped[selectionListId].push(selectionItem);

      return grouped;
    },
    {} as Record<string, TreeItem<string>[]>
  );
};

interface EntityGroupProps {
  report: ReportBuilderDeliverable;
  configSet: ReportEntityConfigColumn[];
  isFilter?: boolean;
}
const getEntityGroup = ({ report, configSet, isFilter }: EntityGroupProps) => {
  type ReportEntity = { type: string; values: string[]; id?: string };
  const mainEntities: ReportEntity[] = configSet.reduce(
    (acc, mainEntityConfig) => {
      const entitySelection = report.selections?.[mainEntityConfig.id] ?? {};
      const groupedSelections = groupSelectionsByListId(entitySelection);

      const id = isFilter ? mainEntityConfig.id : undefined;

      let values = Object.entries(groupedSelections).map(([key, value]) => {
        return {
          type: key as string,
          values: value.map((v) => v.item?.id as string),
          id,
        };
      });
      if (values.length === 0) {
        values = mainEntityConfig.reportEntityOptions.selectionList.filters.map(
          (filter) => ({
            type: filter,
            values: [],
            id,
          })
        );
      }

      return combineCustomSkillValues([...acc, ...values]);
    },
    [] as ReportEntity[]
  );

  return mainEntities;
};

export const generatePayload = (
  report: ReportBuilderDeliverable,
  reportEntityMap: IReportEntityMap
): ReportRequest | null => {
  if (!report || !reportEntityMap) {
    return null;
  }

  const byEntity =
    (entityType: EntityType) => (entityColumn: ReportEntityConfigColumn) =>
      entityColumn.reportEntityOptions.entityType === entityType;

  // @ts-expect-error noUncheckedIndexedAccess
  const mainEntityConfigs = reportEntityMap[report.reportType].columns.filter(
    byEntity(EntityType.MainEntity)
  );

  // @ts-expect-error noUncheckedIndexedAccess
  const comparisonEntityConfigs = reportEntityMap[
    report.reportType
  ].columns.filter(byEntity(EntityType.ComparisonEntity));

  // @ts-expect-error noUncheckedIndexedAccess
  const filterEntityConfigs = reportEntityMap[report.reportType].columns.filter(
    byEntity(EntityType.FilterEntity)
  );

  const mainEntities = getEntityGroup({
    report,
    configSet: mainEntityConfigs,
  }) as EntityInput[];

  const comparisonEntities = getEntityGroup({
    report,
    configSet: comparisonEntityConfigs,
  }) as EntityInput[];

  const filters = getEntityGroup({
    report,
    configSet: filterEntityConfigs,
    isFilter: true,
  }) as ReportFilterInput[];

  const hasComparisonEntities = comparisonEntities.length > 0;
  const hasFilters = filters.length > 0;

  return {
    reportType: report.reportType as ReportTypeEnum,
    mainEntities,
    comparisonEntities: hasComparisonEntities ? comparisonEntities : undefined,
    filters: hasFilters ? filters : undefined,
  };
};
